import styled from '@emotion/styled';
import { Flex } from '@revenuemarket/cds';
import { colorTokens, colors, semanticColors } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { Button, Typography } from 'antd';

import { IItemListItem, IStoreInfo } from '~apis/tax-invoice';
import { css } from '~libs/emotion';

import EditResultFormModal from './TaxInvoiceForm/EditResultFormModal';

type ResultFormTheme = 'red' | 'blue' | 'black';
interface Props {
  theme: ResultFormTheme;
  title: string;
  showEdit?: boolean;
  formItem: Partial<IStoreInfo>;
  onOk?: (data: Partial<IStoreInfo>, checked: boolean) => void;
}

const getThemeColor = (theme: ResultFormTheme) => {
  switch (theme) {
    case 'red':
      return {
        color: '#D13030',
        backgroundColor: '#FFEEEB',
        borderColor: '#FFCAC2',
      };
    case 'blue':
      return {
        color: '#302DCF',
        backgroundColor: '#E8EBFF',
        borderColor: '#BFC7FF',
      };
    case 'black':
      return {
        color: colorTokens.textDescription,
        backgroundColor: colors.grayscale100,
        borderColor: colorTokens.textDescription,
      };
  }
};

export const TaxInvoiceProductForm = ({
  title,
  items,
  representativeItem,
}: {
  title: string;
  items: (Omit<IItemListItem, 'unitPrice' | 'quantity' | 'unit'> & {
    unitPrice?: number;
    quantity?: number;
    unit?: string;
  })[];
  representativeItem?: string;
}) => {
  return (
    <Flex
      style={{
        width: '100%',
      }}
      direction="column"
      align="flex-end"
    >
      <Flex
        style={{
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          width: '100%',
          padding: '8px 0px',
          backgroundColor: getThemeColor('black').backgroundColor,
          borderBottom: `1px solid ${getThemeColor('black').borderColor}`,
        }}
        justify="center"
      >
        <Typography.Text
          style={{
            fontWeight: 'bold',
            color: getThemeColor('black').color,
          }}
        >
          {title}
        </Typography.Text>
      </Flex>
      {items.length > 0
        ? items.map((item) => {
            return (
              <Flex
                direction="column"
                gap={4}
                key={item.name}
                style={{
                  width: '100%',
                  padding: '8px',
                }}
              >
                <Flex justify="space-between">
                  <Typography.Text
                    style={{
                      fontSize: 14,
                    }}
                  >
                    {item.name}
                  </Typography.Text>
                  {item.unitPrice && item.quantity ? (
                    <Typography.Text
                      style={{
                        fontSize: 14,
                      }}
                    >
                      {(item?.unitPrice * item?.quantity).toLocaleString() + '원'}
                    </Typography.Text>
                  ) : (
                    <div />
                  )}
                </Flex>
                {item.unitPrice && item.quantity ? (
                  <Flex justify="space-between">
                    <Typography.Text
                      style={{
                        color: colorTokens.textDescription,
                      }}
                    >
                      {' '}
                      {`${item.quantity}EA x ${item.unitPrice.toLocaleString()}원`}
                    </Typography.Text>
                  </Flex>
                ) : (
                  <div />
                )}
              </Flex>
            );
          })
        : representativeItem && (
            <Flex
              direction="column"
              gap={4}
              style={{
                width: '100%',
                padding: '8px',
              }}
            >
              <Flex justify="space-between">
                <Typography.Text
                  style={{
                    fontSize: 14,
                  }}
                >
                  {representativeItem}
                </Typography.Text>
                <div />
              </Flex>
            </Flex>
          )}
    </Flex>
  );
};

const Value = styled.td`
  font-size: 14px;
  padding: 8px;
  min-width: 100px;
`;

const Label: React.FC<{
  title: string;
  theme: ResultFormTheme;
  colSpan: number;
  isRequired?: boolean;
}> = ({ title, theme, colSpan, isRequired }) => {
  return (
    <td
      className={css`
        min-width: 65px;
        background-color: ${getThemeColor(theme).backgroundColor};
        color: ${getThemeColor(theme).color};
        padding: 8px;
        & span {
          color: ${getThemeColor(theme).color};
        }
      `}
      colSpan={colSpan}
    >
      <Typography.Text>{title}</Typography.Text>
      {isRequired && <Typography.Text>*</Typography.Text>}
    </td>
  );
};

const TaxInvoiceResultForm: React.FC<Props> = ({
  title,
  theme,
  showEdit = true,
  formItem,
  onOk,
}) => {
  const address = formItem?.corpAddress
    ? `${formItem.corpAddress}${
        formItem.corpAddressDetail ? `, ${formItem.corpAddressDetail}` : ''
      }`
    : '';
  return (
    <Flex
      style={{
        width: '100%',
      }}
      direction="column"
      align="flex-end"
    >
      <Flex
        style={{
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          width: '100%',
          padding: '8px 0px',
          backgroundColor: getThemeColor(theme).backgroundColor,
          borderBottom: `1px solid ${getThemeColor(theme).borderColor}`,
        }}
        justify="center"
      >
        <Typography.Text
          style={{
            fontWeight: 'bold',
            color: getThemeColor(theme).color,
          }}
        >
          {title}
        </Typography.Text>
      </Flex>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
        }}
      >
        <tbody>
          <tr
            style={{
              borderBottom: `1px solid ${getThemeColor(theme).borderColor}`,
            }}
          >
            <Label colSpan={1} title="등록번호" theme={theme} isRequired />
            <Value colSpan={1}>
              {showEdit
                ? formItem?.businessRegNo || (
                    <Typography.Text style={{ color: semanticColors.danger.medium }}>
                      번호를 입력하세요
                    </Typography.Text>
                  )
                : formItem?.businessRegNo}
            </Value>
            <Label title="종사업장" theme={theme} colSpan={1} />
            <Value colSpan={1}>{formItem?.taxIdNumber}</Value>
          </tr>
          <tr
            style={{
              borderBottom: `1px solid ${getThemeColor(theme).borderColor}`,
            }}
          >
            <Label colSpan={1} title="상호" theme={theme} isRequired />
            <Value colSpan={1}>
              {showEdit
                ? formItem?.corpName || (
                    <Typography.Text style={{ color: semanticColors.danger.medium }}>
                      상호를 입력하세요
                    </Typography.Text>
                  )
                : formItem?.corpName}
            </Value>
            <Label colSpan={1} title="성명" theme={theme} isRequired />
            <Value colSpan={1}>
              <Typography.Text
                ellipsis
                style={{
                  fontSize: 14,
                  width: 190,
                }}
              >
                {showEdit ? (
                  formItem?.corpCeo?.some((c) => c) ? (
                    formItem?.corpCeo?.join(',')
                  ) : (
                    <Typography.Text style={{ color: semanticColors.danger.medium }}>
                      성명을 입력하세요
                    </Typography.Text>
                  )
                ) : (
                  formItem?.corpCeo?.join(',')
                )}
              </Typography.Text>
            </Value>
          </tr>
          <tr
            style={{
              borderBottom: `1px solid ${getThemeColor(theme).borderColor}`,
            }}
          >
            <Label colSpan={1} title="사업장" theme={theme} isRequired />
            <Value colSpan={3}>
              {showEdit
                ? address || (
                    <Typography.Text style={{ color: semanticColors.danger.medium }}>
                      사업장 주소를 입력하세요
                    </Typography.Text>
                  )
                : address}
            </Value>
          </tr>
          <tr
            style={{
              borderBottom: `1px solid ${getThemeColor(theme).borderColor}`,
            }}
          >
            <Label colSpan={1} title="업태" theme={theme} />
            <Value colSpan={1}>{formItem?.businessType}</Value>
            <Label title="업종" theme={theme} colSpan={1} />
            <Value colSpan={1}>{formItem?.businessClass}</Value>
          </tr>
          <tr
            style={{
              borderBottom: `1px solid ${getThemeColor(theme).borderColor}`,
            }}
          >
            <Label colSpan={1} title="이메일" theme={theme} isRequired />
            <Value colSpan={3}>
              {showEdit ? (
                formItem?.members?.some((m) => m.name) ? (
                  formItem?.members?.map((m) => m?.email).join(',')
                ) : (
                  <Typography.Text style={{ color: semanticColors.danger.medium }}>
                    담당자 정보를 입력하세요
                  </Typography.Text>
                )
              ) : (
                <Flex direction="column">
                  {formItem?.members?.map((m) => (
                    <Typography.Text
                      key={m.email}
                      style={{
                        fontSize: 14,
                      }}
                    >
                      {m.email}
                    </Typography.Text>
                  ))}
                </Flex>
              )}
            </Value>
          </tr>
        </tbody>
      </table>
      {showEdit && (
        <Button
          type="link"
          onClick={() => {
            overlays.open(({ isOpen, onClose }) => (
              <EditResultFormModal
                data={formItem}
                open={isOpen}
                onCancel={onClose}
                onFinished={(data, checked) => {
                  onOk?.(data, checked);
                  onClose();
                }}
                title={title}
                disableFields={['registrationNumber', 'name']}
              />
            ));
          }}
        >{`${title} 정보 수정`}</Button>
      )}
    </Flex>
  );
};

export default TaxInvoiceResultForm;
